/*
 * Live Links page. Mirrors the YouTube playlist's worked-through layout (header
 * with counts + Reset, a grid of unopened cards, a collapsible "opened"
 * section) but each card opens a real browser tab instead of an in-app player.
 */

.links {
  position: relative;
  z-index: 1;
  padding: 0;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  flex: 1;
  min-height: 100%;
  width: 100%;
  display: flex;
  align-items: stretch;
}

.empty {
  position: relative;
  z-index: 1;
  padding: var(--token-space-2xl);
  font-family: var(--token-font-ui);
}

.shell {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--token-space-lg);
  min-height: 0;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--token-space-lg);
  padding: var(--token-space-lg) var(--token-space-xl);
  border-radius: var(--token-radius-card);
  border: 1.5px solid var(--token-accent);
  background: rgba(255, 250, 240, 0.62);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow:
    0 30px 60px -20px rgba(8, 18, 12, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
}

.title {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(30px, 3vw, 54px);
  line-height: 1;
  color: var(--token-accent);
}

.subtitle {
  margin: var(--token-space-xs) 0 0;
  font-family: var(--token-font-body);
  color: rgba(28, 38, 32, 0.68);
}

.actionButton {
  min-height: var(--token-touch-min);
  padding: var(--token-space-sm) var(--token-space-xl);
  border-radius: var(--token-radius-pill);
  border: 1.5px solid color-mix(in srgb, var(--token-accent) 55%, transparent);
  background: var(--token-accent);
  color: var(--token-card-bg);
  font-family: var(--token-font-ui);
  font-weight: 800;
  cursor: pointer;
  flex-shrink: 0;
}

.actionButton:disabled {
  opacity: 0.4;
  cursor: default;
}

.notice {
  padding: var(--token-space-lg) var(--token-space-xl);
  border-radius: var(--token-radius-card);
  background: rgba(255, 250, 240, 0.5);
  font-family: var(--token-font-body);
  color: rgba(28, 38, 32, 0.72);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--token-space-lg);
}

.card {
  display: flex;
  align-items: center;
  gap: var(--token-space-md);
  text-align: left;
  padding: var(--token-space-lg);
  border-radius: var(--token-radius-card);
  border: 1.5px solid color-mix(in srgb, var(--token-accent) 30%, transparent);
  background: rgba(255, 250, 240, 0.7);
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px -18px rgba(8, 18, 12, 0.4);
}

.cardText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.cardText strong {
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cardText span {
  font-size: 12px;
  color: rgba(28, 38, 32, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.openBadge {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 800;
  color: var(--token-accent);
}

.favicon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 6px;
  object-fit: contain;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.faviconLarge {
  width: 36px;
  height: 36px;
  font-size: 22px;
}

.opened {
  border-radius: var(--token-radius-card);
  background: rgba(255, 250, 240, 0.45);
  padding: var(--token-space-md) var(--token-space-lg);
}

.opened > summary {
  cursor: pointer;
  font-weight: 700;
  color: rgba(28, 38, 32, 0.75);
}

.openedList {
  display: flex;
  flex-direction: column;
  gap: var(--token-space-sm);
  margin-top: var(--token-space-md);
}

.openedButton {
  display: flex;
  align-items: center;
  gap: var(--token-space-md);
  text-align: left;
  padding: var(--token-space-sm) var(--token-space-md);
  border-radius: var(--token-radius-pill);
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  font: inherit;
  color: inherit;
  opacity: 0.85;
}

.openedText {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.openedText strong {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.openedText span {
  font-size: 11px;
  color: rgba(28, 38, 32, 0.6);
}
